import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../../../FaIcon';
import {YesNoModal} from '../../../elements/YesNoModal';
import {FactCard} from '../../memory/FactCard';
import {DURABILITY_NOISE_ORDER, durabilityLabelKey} from '../../memory/factConstants';
import {ReviewEditor} from './ReviewCard';

/**
 * "Ruido probable": lo que el triage de durabilidad degradó (`review_priority = LOW`), agrupado por
 * familia. Es el TERCER modo de revisión —al lado de "De a uno" y "Lista completa"—, así que el
 * componente es el PANEL: no se colapsa a sí mismo. Vivía al pie del mazo con su propio plegado, y
 * eso ponía dos clicks entre el usuario y el motivo por el que su material no llegó a la cola (el
 * suyo propio + el de la familia). Quien elige este modo ya pidió ver: mostrar.
 *
 * El mockup no tiene esta sección porque su mazo son siete hechos elegidos a mano. En un cliente
 * real la extracción también trae logística de reunión, números del momento y charla suelta: 40
 * hechos-ruido pasando de a uno por el mazo no sólo cansan, **destruyen el valor del triage** —que
 * existe justamente para que esos no ocupen una decisión cada uno—. Acá se descartan por familia,
 * con un click, y se puede abrir la familia para rescatar el que valga.
 *
 * Vacía si el triage está apagado (nadie es LOW) → la sección no se renderiza y el acto se comporta
 * exactamente como si no existiera.
 *
 * **Las filas se editan acá mismo**, con el editor de la carta (`ReviewEditor`, el mismo que usan
 * el mazo y la "Lista completa"). No es un lujo: `FactCard` en modo `queue` ofrece "Editar" siempre
 * —no sabe si quien la monta tiene editor— y sólo avisa hacia arriba. Sin editor, el click no
 * mostraba nada, pero dejaba al acto con una edición abierta que nadie veía: se apagaba el gesto
 * del mazo, sus dos nubs quedaban deshabilitados y el teclado salía temprano. La única salida era
 * abrir y cancelar la edición de otra carta.
 *
 * `maxPerBatch` recorta los ids que se mandan a descartar al tope del bulk del backend. Se recorta
 * ACÁ y no aguas abajo porque la confirmación dice el número: prometer 240 y rechazar 200 es peor
 * que rechazar 200 diciéndolo.
 */
export function NoiseQueueSection({
    noise, documentsById, isBusy, editingId, maxPerBatch = Infinity,
    onApprove, onReject, onEdit, onCancelEdit, onSaveEdit, onDiscardGroup,
    // «Ver la cita» sobre la fuente: lo recibe la card y abre el visor único de la página.
    onOpenSource,
}) {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState({});
    const [discarding, setDiscarding] = useState(null);

    if (!noise || noise.length === 0) return null;

    const byCategory = {};
    noise.forEach((f) => {
        const cat = DURABILITY_NOISE_ORDER.includes(f.durability_class) ? f.durability_class : 'OTHER';
        (byCategory[cat] = byCategory[cat] || []).push(f);
    });
    const groups = [...DURABILITY_NOISE_ORDER, 'OTHER']
        .map(cat => ({cat, items: byCategory[cat] || []}))
        .filter(g => g.items.length > 0);

    return (
        <section className="iabr-noise">
            {/* Qué es esto y por qué está acá, a la vista y no detrás del plegado. Pablo lo
                preguntó mirando la pantalla («¿Por qué me aparece?»): la explicación existía, pero
                vivía en la cabecera que había que desplegar para leerla. El rótulo ya lo pone el
                botón del modo, así que acá va sólo el porqué. */}
            <p className="iabr-noise-lede">{t('ia_booster.deck.noise_hint')}</p>

            {groups.map((group) => {
                const isOpen = !!expanded[group.cat];
                const label = t(durabilityLabelKey(group.cat));
                return (
                    <div key={group.cat} className="iabr-noise-group">
                        <div className="iabr-noise-group-head">
                            <button type="button" className="iabr-noise-group-toggle"
                                    aria-expanded={isOpen}
                                    onClick={() => setExpanded(prev => ({
                                        ...prev, [group.cat]: !prev[group.cat],
                                    }))}>
                                <FaIcon iconName={isOpen ? 'chevron-down' : 'chevron-right'}/>
                                <span className="iabr-noise-group-title">{label}</span>
                                <span className="iabr-noise-group-count">{group.items.length}</span>
                            </button>
                            <button type="button" className="iabr-link-danger"
                                    onClick={() => setDiscarding({
                                        label,
                                        ids: group.items.slice(0, maxPerBatch).map(f => f.id),
                                    })}>
                                {t('ia_booster.deck.noise_discard_group')}
                            </button>
                        </div>
                        {isOpen && group.items.map(fact => (editingId === fact.id ? (
                            <ReviewEditor key={fact.id} fact={fact} busy={isBusy(fact.id)}
                                          variant="row"
                                          onSave={onSaveEdit} onCancel={onCancelEdit}/>
                        ) : (
                            <FactCard key={fact.id} fact={fact} mode="queue"
                                      documentsById={documentsById}
                                      busy={isBusy(fact.id)}
                                      onApprove={onApprove} onReject={onReject} onEdit={onEdit}
                                      onOpenSource={onOpenSource}/>
                        )))}
                    </div>
                );
            })}

            <YesNoModal
                skipRenderButton
                // El modal portalea a `<body>`, fuera del alcance de `.iabr-deck`: sin esta clase
                // sale con la superficie clara de la app y los dos botones en el mismo naranja
                // (QA-VIS-booster-02). El componente es compartido y no se toca — sólo se le da
                // el gancho que ya expone.
                className="iabr-confirm"
                isOpen={!!discarding}
                title={t('ia_booster.deck.noise_confirm_title')}
                question={discarding ? t('ia_booster.deck.noise_confirm_question', {
                    n: discarding.ids.length,
                    group: discarding.label,
                    interpolation: {escapeValue: false},
                }) : ''}
                onYesAnswer={() => {
                    const ids = discarding.ids;
                    setDiscarding(null);
                    onDiscardGroup(ids);
                }}
                onNoAnswer={() => setDiscarding(null)}
            />
        </section>
    );
}

export default NoiseQueueSection;
